
import { AuthenticationResult } from '@azure/msal-browser';
import { msalInstance } from './../index';
import { userPermissionScopes } from "../AuthConfig";
import { ViewModel, EmbedTokenResult, ActivityLogEntry, User, ExportFileRequest } from '../models/models';
import ManagementAuth from './ManagementAuth';

export default class AppOwnsDataWebApi {

  public static ApiRoot: string = window.location.hostname === "localhost" ? "https://localhost:44302/api/" : "/api/";

  private static GetAccessToken = async (): Promise<string> => {

    const managementToken = ManagementAuth.getToken();
    if (managementToken) {
      return managementToken;
    }

    const account = msalInstance?.getActiveAccount();

    if (account) {
      let authResult: AuthenticationResult;
      try {
        // try to acquire access token from MSAL cache first
        authResult = await msalInstance.acquireTokenSilent({ scopes: userPermissionScopes, account: account });
      }
      catch (silentError) {
        console.warn("Silent token acquisition failed. Redirecting to acquire token...", silentError);
        // if access token not available in cache, redirect to acquire token (bypasses popup blockers)
        await msalInstance.acquireTokenRedirect({ scopes: userPermissionScopes, account: account });
        throw new Error("Redirecting to Microsoft login...");
      }
      // return access token from authentication result 
      return authResult.accessToken;
    }
    else {
      return "";
    }

  };

  static LoginUser = async (LoginId: string, UserName: string): Promise<void> => {

    var user = new User();
    user.LoginId = LoginId;
    user.UserName = UserName;

    var accessToken: string = await AppOwnsDataWebApi.GetAccessToken();
    var postData: string = JSON.stringify(user);

    var restUrl = AppOwnsDataWebApi.ApiRoot + "UserLogin/";

    await fetch(restUrl, {
      method: "POST",
      body: postData,
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
        "Authorization": "Bearer " + accessToken
      }
    });

    return;
  }

  static GetEmbeddingData = async (): Promise<ViewModel> => {

    var accessToken: string = await AppOwnsDataWebApi.GetAccessToken();
    var restUrl = AppOwnsDataWebApi.ApiRoot + "Embed/";

    let response = await fetch(restUrl, {
      method: "GET",
      headers: {
        "Accept": "application/json;",
        "Authorization": "Bearer " + accessToken
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Server returned error ${response.status}: ${errorText || response.statusText}`);
    }

    return await response.json();
  }

  static GetEmbedToken = async (): Promise<EmbedTokenResult> => {

    var accessToken: string = await AppOwnsDataWebApi.GetAccessToken();
    var restUrl = AppOwnsDataWebApi.ApiRoot + "EmbedToken/";

    let response = await fetch(restUrl, {
      method: "GET",
      headers: {
        "Accept": "application/json;",
        "Authorization": "Bearer " + accessToken
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Server returned error ${response.status}: ${errorText || response.statusText}`);
    }

    return await response.json();
  }

  static LogActivity = async (activityLogEntry: ActivityLogEntry): Promise<void> => {

    var accessToken: string = await AppOwnsDataWebApi.GetAccessToken();
    var postData: string = JSON.stringify(activityLogEntry);
    var restUrl = AppOwnsDataWebApi.ApiRoot + "ActivityLog/";

    let fetchResponse = await fetch(restUrl, {
      method: "POST",
      body: postData,
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
        "Authorization": "Bearer " + accessToken
      }
    });

    return;

  }

  static ExportFile = async (ExportRequest: ExportFileRequest): Promise<void> => {

    var restUrl: string = AppOwnsDataWebApi.ApiRoot + "ExportFile/";
    var accessToken: string = await AppOwnsDataWebApi.GetAccessToken();

    // prepare JSON body for POST request to retrieve exported report file
    var postData: string = JSON.stringify(ExportRequest);

    // execute POST request synchronously to retrieve exported report file
    let fetchResponse = await fetch(restUrl, {
      method: "POST",
      body: postData,
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
        "Authorization": "Bearer " + accessToken
      }
    });

    if (!fetchResponse.ok) {
      const errorText = await fetchResponse.text();
      throw new Error(`Server returned error ${fetchResponse.status}: ${errorText || fetchResponse.statusText}`);
    }

    // Once POST call returns, get file name from HTTP response
    const header = fetchResponse.headers.get('Content-Disposition');
    if (!header) {
      throw new Error("Invalid response from server: missing Content-Disposition header");
    }
    const parts = header.split(';');
    if (parts.length < 2) {
      throw new Error("Invalid Content-Disposition header format");
    }
    let filename = parts[1].split('=')[1].replace(/['"]/g, '').trim();

    // get blob with export file content
    let blob = await fetchResponse.blob();

    // trigger export file download in browser window
    var url = window.URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();

    // return control to caller using await
    return;
  }

}



